import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  acceptUnitRequestAgreement,
  cancelBnbBooking,
  cancelUnitRequest,
  getMyBnbBookings,
  getMyUnitRequests,
  getPropertyById,
  getUnitRequestPaymentDetails,
  submitUnitRequestPaymentsEvidence,
  submitBnbBookingPaymentEvidence,
  uploadPaymentEvidencePhoto,
} from '../../../api/services/propertyService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import {
  MdApartment,
  MdCalendarToday,
  MdAttachMoney,
  MdInfoOutline,
} from 'react-icons/md';
import './UnitRequests.css';

function formatMoneyKES(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return `KES ${amount.toLocaleString()}`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const REQUEST_TYPE_OPTIONS = [
  { value: 'all', label: 'All requests' },
  { value: 'rental', label: 'Rental requests' },
  { value: 'bnb', label: 'BnB requests' },
];

function isBnbRequest(req) {
  if (!req || typeof req !== 'object') return false;
  if (req.requestType === 'bnb') return true;
  if (req.requestType === 'rental') return false;
  const hasStayDates = Boolean(req.checkInDate || req.checkOutDate);
  const hasNightPrice = typeof req.pricePerNight === 'number' && Number.isFinite(req.pricePerNight);
  const hasBookingTotals = ['subtotal', 'serviceFee', 'taxAmount', 'totalAmount'].some(
    (key) => typeof req[key] === 'number' && Number.isFinite(req[key])
  );
  return hasStayDates || hasNightPrice || hasBookingTotals;
}

const UnitRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [agreeingRequestId, setAgreeingRequestId] = useState(null);
  const [confirmAgreementRequest, setConfirmAgreementRequest] = useState(null);
  const [confirmAgreementChecked, setConfirmAgreementChecked] = useState(false);
  const [confirmCancelRequest, setConfirmCancelRequest] = useState(null);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [confirmCancelBnbBooking, setConfirmCancelBnbBooking] = useState(null);
  const [bnbCancelReason, setBnbCancelReason] = useState('');
  const [cancellingBnbBookingId, setCancellingBnbBookingId] = useState(null);
  const [evidenceModalRequest, setEvidenceModalRequest] = useState(null);
  const [evidenceModalMessage, setEvidenceModalMessage] = useState('');
  const evidenceModalMessageTimeoutRef = useRef(null);
  const [loadingPaymentDetailsById, setLoadingPaymentDetailsById] = useState({});
  const [paymentDetailsById, setPaymentDetailsById] = useState({});
  const [paymentsDraftById, setPaymentsDraftById] = useState({});
  const [uploadingEvidenceById, setUploadingEvidenceById] = useState({});
  const [submittingEvidenceRequestId, setSubmittingEvidenceRequestId] = useState(null);
  const [paymentDetailsModalRequest, setPaymentDetailsModalRequest] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const [rentalResult, bnbResult] = await Promise.allSettled([
        getMyUnitRequests(),
        getMyBnbBookings(),
      ]);

      const rentalList = rentalResult.status === 'fulfilled'
        ? (() => {
            const payload = rentalResult.value?.data ?? rentalResult.value;
            const list = Array.isArray(payload) ? payload : payload?.data ?? [];
            return Array.isArray(list) ? list.map((item) => ({ ...item, requestType: 'rental' })) : [];
          })()
        : [];

      const bnbList = bnbResult.status === 'fulfilled'
        ? (() => {
            const payload = bnbResult.value?.data ?? bnbResult.value;
            const list = Array.isArray(payload) ? payload : payload?.data ?? [];
            return Array.isArray(list) ? list.map((item) => ({ ...item, requestType: 'bnb' })) : [];
          })()
        : [];

      if (rentalResult.status === 'rejected' && bnbResult.status === 'rejected') {
        throw new Error(
          rentalResult.reason?.message ||
          bnbResult.reason?.message ||
          'Failed to load your requests.'
        );
      }

      const merged = [...rentalList, ...bnbList].sort((a, b) => {
        const aTs = new Date(a?.createdAt || 0).getTime();
        const bTs = new Date(b?.createdAt || 0).getTime();
        return (Number.isFinite(bTs) ? bTs : 0) - (Number.isFinite(aTs) ? aTs : 0);
      });
      setRequests(merged);
    } catch (e) {
      setError(e?.message || 'Failed to load your unit requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key !== 'Escape') return;
      if (confirmAgreementRequest) {
        setConfirmAgreementRequest(null);
        setConfirmAgreementChecked(false);
      }
      if (confirmCancelRequest) {
        setConfirmCancelRequest(null);
      }
      if (confirmCancelBnbBooking) {
        setConfirmCancelBnbBooking(null);
        setBnbCancelReason('');
      }
      if (evidenceModalRequest) {
        setEvidenceModalRequest(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmAgreementRequest, confirmCancelRequest, confirmCancelBnbBooking, evidenceModalRequest]);

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const statusOk =
        statusFilter === 'all' || (r?.status || '').toUpperCase() === statusFilter;
      if (!statusOk) return false;

      if (requestTypeFilter === 'bnb') return isBnbRequest(r);
      if (requestTypeFilter === 'rental') return !isBnbRequest(r);
      return true;
    });
  }, [requests, statusFilter, requestTypeFilter]);

  const handleAcceptAgreement = async (req) => {
    if (!req?.id) return;
    try {
      setAgreeingRequestId(req.id);
      setError('');
      setSuccessMessage('');
      const res = await acceptUnitRequestAgreement(req.id);
      setSuccessMessage(res?.message || 'Agreement accepted.');
      await fetchRequests();
    } catch (err) {
      setError(err?.message || 'Failed to accept agreement.');
    } finally {
      setAgreeingRequestId(null);
    }
  };

  const openAcceptAgreementModal = (e, req) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    setError('');
    setSuccessMessage('');
    setConfirmAgreementRequest(req);
    setConfirmAgreementChecked(false);
  };

  const closeAcceptAgreementModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setConfirmAgreementRequest(null);
    setConfirmAgreementChecked(false);
  };

  const confirmAcceptAgreement = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const req = confirmAgreementRequest;
    if (!req?.id) return;
    if (!confirmAgreementChecked) return;
    await handleAcceptAgreement(req);
    closeAcceptAgreementModal();
  };

  const isCancellable = (req) => {
    const status = String(req?.status || '').toUpperCase();
    return status === 'REQUESTED' || status === 'PENDING';
  };

  const isBnbBookingCancellable = (req) => {
    if (!isBnbRequest(req)) return false;
    const status = String(req?.status || '').toUpperCase();
    return status === 'PENDING' || status === 'APPROVED';
  };

  const openCancelModal = (e, req) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    setError('');
    setSuccessMessage('');
    setConfirmCancelRequest(req);
  };

  const closeCancelModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setConfirmCancelRequest(null);
  };

  const openCancelBnbModal = (e, req) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    setError('');
    setSuccessMessage('');
    setBnbCancelReason('');
    setConfirmCancelBnbBooking(req);
  };

  const closeCancelBnbModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setConfirmCancelBnbBooking(null);
    setBnbCancelReason('');
  };

  const confirmCancelBnb = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const req = confirmCancelBnbBooking;
    if (!req?.id) return;
    const reason = String(bnbCancelReason || '').trim();
    if (!reason) {
      setError('Please enter a reason for cancelling.');
      return;
    }
    try {
      setCancellingBnbBookingId(req.id);
      setError('');
      setSuccessMessage('');
      const res = await cancelBnbBooking(req.id, { reason });
      setSuccessMessage(res?.message || 'Booking cancelled.');
      await fetchRequests();
      closeCancelBnbModal();
    } catch (err) {
      setError(err?.message || 'Failed to cancel booking.');
    } finally {
      setCancellingBnbBookingId(null);
    }
  };

  const confirmCancel = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const req = confirmCancelRequest;
    if (!req?.id) return;
    try {
      setCancellingRequestId(req.id);
      setError('');
      setSuccessMessage('');
      const res = await cancelUnitRequest(req.id);
      setSuccessMessage(res?.message || 'Request cancelled.');
      await fetchRequests();
      closeCancelModal();
    } catch (err) {
      setError(err?.message || 'Failed to cancel request.');
    } finally {
      setCancellingRequestId(null);
    }
  };

  const openEvidenceModal = (e, req) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    setError('');
    setSuccessMessage('');
    setEvidenceModalMessage('');
    ensureDraft(req);
    setEvidenceModalRequest(req);
  };

  const closeEvidenceModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEvidenceModalRequest(null);
    setEvidenceModalMessage('');
    if (evidenceModalMessageTimeoutRef.current) {
      clearTimeout(evidenceModalMessageTimeoutRef.current);
      evidenceModalMessageTimeoutRef.current = null;
    }
  };

  const flashEvidenceModalMessage = (message) => {
    if (evidenceModalMessageTimeoutRef.current) {
      clearTimeout(evidenceModalMessageTimeoutRef.current);
    }
    setEvidenceModalMessage(message);
    evidenceModalMessageTimeoutRef.current = setTimeout(() => {
      setEvidenceModalMessage('');
      evidenceModalMessageTimeoutRef.current = null;
    }, 2200);
  };

  const removeEvidencePhoto = (e, requestId, section) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requestId) return;
    onChangeDraftField(requestId, section, 'photoUrl', '');
    if (evidenceModalRequest?.id === requestId) {
      flashEvidenceModalMessage('Photo removed.');
    }
  };

  const openAgreement = async (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) return;
    setError('');
    setSuccessMessage('');

    // Open the tab immediately to avoid popup blockers, then load the authenticated blob URL.
    // Note: using `noopener` here can make browsers return `null` for the window handle,
    // which prevents us from navigating the already-opened tab after the async fetch.
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      try {
        newWindow.document.title = 'Agreement preview';
        newWindow.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        newWindow.document.body.style.padding = '16px';
        newWindow.document.body.textContent = 'Loading agreement…';
      } catch {
        // Ignore if browser blocks document access.
      }
    }
    try {
      const blobUrl = await fetchAuthenticatedImage(url);
      const targetUrl = blobUrl || url;

      if (newWindow) {
        // Best-effort hardening: detach opener after we’re done navigating.
        try {
          newWindow.opener = null;
        } catch {
          /* ignore */
        }
        newWindow.location.href = targetUrl;
      } else {
        // If the browser blocked the initial window, this will likely also be blocked,
        // but it's still the best fallback we have.
        window.open(targetUrl, '_blank');
      }
    } catch (err) {
      if (newWindow) {
        newWindow.close();
      }
      setError(err?.message || 'Failed to open agreement preview.');
    }
  };

  const openProtectedFile = async (e, url, title = 'Preview') => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) return;
    setError('');

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      try {
        newWindow.document.title = title;
        newWindow.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        newWindow.document.body.style.padding = '16px';
        newWindow.document.body.textContent = `Loading ${title.toLowerCase()}…`;
      } catch {
        /* ignore */
      }
    }

    try {
      const blobUrl = await fetchAuthenticatedImage(url);
      const targetUrl = blobUrl || url;
      if (newWindow) {
        try {
          newWindow.opener = null;
        } catch {
          /* ignore */
        }
        newWindow.location.href = targetUrl;
      } else {
        window.open(targetUrl, '_blank');
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      setError(err?.message || `Failed to open ${title.toLowerCase()}.`);
    }
  };

  const loadPaymentDetails = async (e, req) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const requestId = req?.id;
    if (!requestId || paymentDetailsById?.[requestId]) return;
    try {
      setLoadingPaymentDetailsById((prev) => ({ ...prev, [requestId]: true }));
      let paymentData = null;
      if (isBnbRequest(req)) {
        const propId = req.propertyId || req.property?.id;
        if (propId) {
          const res = await getPropertyById(propId, false);
          paymentData = res?.data?.paymentDetails || null;
        } else {
          throw new Error('Property ID not found for this booking.');
        }
      } else {
        const res = await getUnitRequestPaymentDetails(requestId);
        paymentData = res?.data || null;
      }
      setPaymentDetailsById((prev) => ({ ...prev, [requestId]: paymentData }));
    } catch (err) {
      setError(err?.message || 'Failed to load payment details.');
    } finally {
      setLoadingPaymentDetailsById((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const openPaymentDetailsModal = async (e, req) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!req || !req.id) return;
    if (!paymentDetailsById?.[req.id]) {
      await loadPaymentDetails(null, req);
    }

    setPaymentDetailsModalRequest(req);
  };

  const closePaymentDetailsModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPaymentDetailsModalRequest(null);
  };

  const ensureDraft = (req) => {
    if (!req?.id) return;
    setPaymentsDraftById((prev) => {
      if (prev[req.id]) return prev;
      return {
        ...prev,
        [req.id]: {
          deposit: {
            amountPaid: (isBnbRequest(req) ? req.totalAmount : req.depositAmount) ?? '',
            mpesaMessage: req.depositEvidenceText ?? '',
            photoUrl: req.depositEvidenceUrl ?? '',
          },
          firstMonth: {
            amountPaid: req.monthlyRent ?? '',
            mpesaMessage: req.firstMonthEvidenceText ?? '',
            photoUrl: req.firstMonthEvidenceUrl ?? '',
          },
        },
      };
    });
  };

  const onChangeDraftField = (requestId, section, field, value) => {
    setPaymentsDraftById((prev) => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [section]: {
          ...((prev[requestId] || {})[section] || {}),
          [field]: value,
        },
      },
    }));
  };

  const uploadEvidence = async (e, req, section) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setError('');
      setSuccessMessage('');
      setUploadingEvidenceById((prev) => ({ ...prev, [`${req.id}:${section}`]: true }));
      const res = await uploadPaymentEvidencePhoto(file);
      const url = res?.data?.url || res?.data || '';
      onChangeDraftField(req.id, section, 'photoUrl', url);
      if (evidenceModalRequest?.id === req.id) {
        flashEvidenceModalMessage('Photo uploaded.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to upload photo.');
    } finally {
      setUploadingEvidenceById((prev) => ({ ...prev, [`${req.id}:${section}`]: false }));
      e.target.value = '';
    }
  };

  const submitEvidence = async (e, req) => {
    e.preventDefault();
    e.stopPropagation();
    if (!req?.id) return;
    ensureDraft(req);
    const draft = paymentsDraftById?.[req.id];
    if (!draft) return;

    const isBnb = isBnbRequest(req);
    const deposit = {
      amountPaid: draft.deposit?.amountPaid === '' ? undefined : Number(draft.deposit?.amountPaid),
      mpesaMessage: draft.deposit?.mpesaMessage || '',
      photoUrl: draft.deposit?.photoUrl || '',
    };
    const firstMonth = {
      amountPaid: draft.firstMonth?.amountPaid === '' ? undefined : Number(draft.firstMonth?.amountPaid),
      mpesaMessage: draft.firstMonth?.mpesaMessage || '',
      photoUrl: draft.firstMonth?.photoUrl || '',
    };

    if (isBnb) {
      if (!deposit.mpesaMessage.trim() && !deposit.photoUrl.trim()) {
        setError('Please provide an Mpesa message or photo for the booking payment.');
        return;
      }

      const bnbPayload = {
        amount: Number(deposit.amountPaid) || Number(req.totalAmount) || 0,
        ...(deposit.mpesaMessage.trim() ? { mpesaMessage: deposit.mpesaMessage.trim() } : {}),
        ...(deposit.photoUrl.trim() ? { photoUrl: deposit.photoUrl.trim() } : {}),
      };

      try {
        setSubmittingEvidenceRequestId(req.id);
        setError('');
        setSuccessMessage('');
        const res = await submitBnbBookingPaymentEvidence(req.id, bnbPayload);
        setSuccessMessage(res?.message || 'Payment evidence submitted.');
        await fetchRequests();
        if (evidenceModalRequest?.id === req.id) {
          setEvidenceModalRequest(null);
        }
      } catch (err) {
        setError(err?.message || 'Failed to submit payment evidence.');
      } finally {
        setSubmittingEvidenceRequestId(null);
      }
      return;
    }

    const payload = {
      deposit: {
        ...(Number.isFinite(deposit.amountPaid) ? { amountPaid: deposit.amountPaid } : {}),
        ...(deposit.mpesaMessage.trim() ? { mpesaMessage: deposit.mpesaMessage.trim() } : {}),
        ...(deposit.photoUrl.trim() ? { photoUrl: deposit.photoUrl.trim() } : {}),
      },
      firstMonth: {
        ...(Number.isFinite(firstMonth.amountPaid) ? { amountPaid: firstMonth.amountPaid } : {}),
        ...(firstMonth.mpesaMessage.trim() ? { mpesaMessage: firstMonth.mpesaMessage.trim() } : {}),
        ...(firstMonth.photoUrl.trim() ? { photoUrl: firstMonth.photoUrl.trim() } : {}),
      }
    };

    const hasDepositEvidence = Boolean(payload.deposit.mpesaMessage || payload.deposit.photoUrl);
    const hasFirstEvidence = Boolean(payload.firstMonth.mpesaMessage || payload.firstMonth.photoUrl);
    if (!hasDepositEvidence || !hasFirstEvidence) {
      setError('Please provide Mpesa message or photo for both Deposit and First month (photo optional).');
      return;
    }

    try {
      setSubmittingEvidenceRequestId(req.id);
      setError('');
      setSuccessMessage('');
      const res = await submitUnitRequestPaymentsEvidence(req.id, payload);
      setSuccessMessage(res?.message || 'Payment evidence submitted.');
      await fetchRequests();
      if (evidenceModalRequest?.id === req.id) {
        setEvidenceModalRequest(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to submit payment evidence.');
    } finally {
      setSubmittingEvidenceRequestId(null);
    }
  };

  return (
    <div className="unit-requests-page">
      <CollapsibleTopPanel id="tenant-unit-requests-top" defaultOpenOnMobile={false}>
        <div className="unit-requests-header">
          <h1 className="unit-requests-title">My unit requests</h1>
          <p className="unit-requests-subtitle">Track and manage your rental and BnB requests</p>
        </div>

        <div className="unit-requests-toolbar">
          <div className="unit-requests-filters">
            <div className="unit-requests-filter-group">
              <label className="unit-requests-filter-label" htmlFor="unit-requests-status">
                Status
              </label>
              <select
                id="unit-requests-status"
                className="unit-requests-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="unit-requests-filter-group">
              <label className="unit-requests-filter-label" htmlFor="unit-requests-type">
                Type
              </label>
              <select
                id="unit-requests-type"
                className="unit-requests-filter-select"
                value={requestTypeFilter}
                onChange={(e) => setRequestTypeFilter(e.target.value)}
                disabled={loading}
              >
                {REQUEST_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </CollapsibleTopPanel>

      {loading && (
        <div className="unit-requests-state unit-requests-loading">
          <div className="unit-requests-spinner" aria-hidden />
          <p>Loading your requests…</p>
        </div>
      )}

      {!loading && error && (
        <div className="unit-requests-state unit-requests-error">
          <p>{error}</p>
          <button type="button" className="unit-requests-retry" onClick={fetchRequests}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && successMessage && !confirmAgreementRequest && !evidenceModalRequest && !confirmCancelBnbBooking && (
        <div className="unit-requests-state unit-requests-success">
          <p>{successMessage}</p>
        </div>
      )}

      {!loading && !error && filteredRequests.length === 0 && (
        <div className="unit-requests-state unit-requests-empty">
          <MdApartment aria-hidden />
          <p>
            {statusFilter === 'all' && requestTypeFilter === 'all'
              ? 'You have no unit requests yet.'
              : `No ${requestTypeFilter === 'all' ? '' : `${requestTypeFilter} `}${statusFilter.toLowerCase()} requests.`}
          </p>
          <button
            type="button"
            className="unit-requests-browse"
            onClick={() => navigate('/tenant/listings')}
          >
            Browse listings
          </button>
        </div>
      )}

      {!loading && !error && filteredRequests.length > 0 && (
        <div className="unit-requests-list">
          {filteredRequests.map((req) => (
            <article
              key={req.id}
              className="unit-request-card"
            >
              <div className="unit-request-card-body">
                <div className="unit-request-card-main">
                <div className="unit-request-card-header">
                  <h3 className="unit-request-property">{req.propertyName || 'Property'}</h3>
                  <span className={`unit-request-status unit-request-status--${(req.status || '').toLowerCase()}`}>
                    {req.status || '—'}
                  </span>
                </div>
                <div className="unit-request-unit-row">
                  <MdApartment className="unit-request-icon" aria-hidden />
                  <span>{req.unitName || req.unitNumber || 'Unit'}</span>
                </div>
                <div className="unit-request-meta">
                  <span className="unit-request-meta-item">
                    <MdCalendarToday aria-hidden />
                    {formatDate(req.createdAt)}
                  </span>
                  <span className="unit-request-meta-item">
                    <MdAttachMoney aria-hidden />
                    {isBnbRequest(req)
                      ? (formatMoneyKES(req.totalAmount) || formatMoneyKES(req.pricePerNight) || '—')
                      : `${formatMoneyKES(req.monthlyRent) || '—'}/mo`}
                  </span>
                </div>
                {isBnbRequest(req) && (
                  <div className="unit-request-meta">
                    <span className="unit-request-meta-item">
                      <MdCalendarToday aria-hidden />
                      Stay: {formatDate(req.checkInDate)} - {formatDate(req.checkOutDate)}
                    </span>
                  </div>
                )}
                {isBnbRequest(req) && (
                  <div className="unit-request-meta">
                    <span className="unit-request-meta-item">
                      <MdAttachMoney aria-hidden />
                      Service fee: {formatMoneyKES(req.serviceFee) || '—'}
                    </span>
                    <span className="unit-request-meta-item">
                      <MdAttachMoney aria-hidden />
                      Tax: {formatMoneyKES(req.taxAmount) || '—'}
                    </span>
                  </div>
                )}
                {isBnbRequest(req) && req.bookingCode && (
                  <div className="unit-request-meta">
                    <span className="unit-request-meta-item">
                      <MdInfoOutline aria-hidden />
                      Booking code: {req.bookingCode}
                    </span>
                  </div>
                )}
                {req.notes && (
                  <div className="unit-request-notes">
                    <MdInfoOutline aria-hidden />
                    <span>{req.notes}</span>
                  </div>
                )}

                {String(req.status || '').toUpperCase() === 'REJECTED' && (
                  <div className="unit-request-rejection">
                    <MdInfoOutline aria-hidden />
                    <div>
                      <div className="unit-request-rejection-title">Rejection reason</div>
                      <div className="unit-request-rejection-text">{req.rejectionReason || req.reason || req.cancelReason || req.rejection_reason || '—'}</div>
                    </div>
                  </div>
                )}

                {String(req.status || '').toUpperCase() === 'APPROVED' &&
                  !req.agreementAccepted &&
                  req.agreementUrl && (
                    <div className="unit-request-agreement">
                      <div className="unit-request-agreement-title">Agreement required</div>
                      <div className="unit-request-agreement-actions">
                        <button
                          type="button"
                          className="unit-request-agreement-btn unit-request-agreement-btn--secondary"
                          onClick={(e) => openAgreement(e, req.agreementUrl)}
                        >
                          View agreement
                        </button>
                        <button
                          type="button"
                          className="unit-request-agreement-btn unit-request-agreement-btn--primary"
                          onClick={(e) => openAcceptAgreementModal(e, req)}
                          disabled={agreeingRequestId === req.id}
                        >
                          {agreeingRequestId === req.id ? 'Accepting…' : 'Accept agreement'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="unit-request-card-aside">
                  <div className="unit-request-deposit">
                    <span className="unit-request-deposit-label">{isBnbRequest(req) ? 'Total' : 'Deposit'}</span>
                    <span className="unit-request-deposit-value">
                      {isBnbRequest(req)
                        ? (formatMoneyKES(req.totalAmount) || '—')
                        : (formatMoneyKES(req.depositAmount) || '—')}
                    </span>
                  </div>
                </div>
              </div>

              {!isBnbRequest(req) && String(req.status || '').toUpperCase() === 'ASSIGNED' && (
                <div className="unit-request-card-payment">
                  <div className="unit-request-payment unit-request-payment--success">
                    <div className="unit-request-payment-title">Assignment complete</div>
                    <p className="unit-request-payment-lead">
                      Your payment has been verified. You have been officially assigned to the unit <strong>{req.unitName || req.unitNumber || 'this unit'}</strong>.
                    </p>
                  </div>
                </div>
              )}

              {!isBnbRequest(req) &&
                String(req.status || '').toUpperCase() !== 'ASSIGNED' &&
                (String(req.status || '').toUpperCase() === 'AGREEMENT_ACCEPTED' ||
                  req.agreementAccepted ||
                  (String(req.status || '').toUpperCase() === 'APPROVED' && !req.agreementUrl)) && (
                <div className="unit-request-card-payment">
                  <div className="unit-request-payment">
                    <div className="unit-request-payment-title">Pay deposit &amp; first month</div>
                    <p className="unit-request-payment-lead">
                      Load payment instructions, complete both payments, then submit M-Pesa or photo proof in one step.
                    </p>

                    <div className="unit-request-payment-stack">
                      <section className="unit-request-payment-step" aria-labelledby={`ur-pay-${req.id}`}>
                        <h4 className="unit-request-payment-step-title" id={`ur-pay-${req.id}`}>
                          <span className="unit-request-payment-step-num">1</span>
                          Payment details
                        </h4>
                        <button
                          type="button"
                          className="unit-request-payment-btn"
                          onClick={(e) => openPaymentDetailsModal(e, req)}
                          disabled={Boolean(loadingPaymentDetailsById?.[req.id])}
                        >
                          {loadingPaymentDetailsById?.[req.id] ? 'Loading…' : 'Show payment details'}
                        </button>
                      </section>

                      <section className="unit-request-payment-step" aria-labelledby={`ur-ev-${req.id}`}>
                        <h4 className="unit-request-payment-step-title" id={`ur-ev-${req.id}`}>
                          <span className="unit-request-payment-step-num">2</span>
                          Evidence
                        </h4>
                        {String(req.status || '').toUpperCase() === 'PAYMENT_EVIDENCE_SUBMITTED' ||
                        req.depositEvidenceText ||
                        req.depositEvidenceUrl ||
                        req.firstMonthEvidenceText ||
                        req.firstMonthEvidenceUrl ? (
                          <div className="unit-request-evidence-status" onClick={(e) => e.stopPropagation()}>
                            <div className="unit-request-evidence-status-label">Submitted</div>
                            <div className="unit-request-evidence-status-text">Waiting for landlord or agent to confirm.</div>
                          </div>
                        ) : (
                          <div className="unit-request-evidence-cta unit-request-evidence-cta--inline" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="unit-request-evidence-open" onClick={(e) => openEvidenceModal(e, req)}>
                              Submit payment evidence
                            </button>
                            <p className="unit-request-evidence-hint unit-request-evidence-hint--tight">
                              Opens a form for deposit and first month (M-Pesa message or photo for each).
                            </p>
                          </div>
                        )}
                      </section>
                    </div>
                  </div>
                </div>
              )}

              {/* ── BnB Booking: Payment evidence submission ── */}
              {isBnbRequest(req) && String(req.status || '').toUpperCase() === 'CONFIRMED' && (
                <div className="unit-request-card-payment">
                  <div className="unit-request-payment unit-request-payment--success">
                    <div className="unit-request-payment-title">Stay confirmed</div>
                    <p className="unit-request-payment-lead">
                      Your payment has been verified. Your stay is confirmed for <strong>{formatDate(req.checkInDate)} - {formatDate(req.checkOutDate)}</strong>.
                    </p>
                  </div>
                </div>
              )}

              {((isBnbRequest(req) && isBnbBookingCancellable(req)) || (!isBnbRequest(req) && isCancellable(req))) && (
                <div className="unit-request-card-actions unit-request-card-actions--footer" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="unit-request-action-btn unit-request-action-btn--danger"
                    onClick={(e) => (isBnbRequest(req) ? openCancelBnbModal(e, req) : openCancelModal(e, req))}
                    disabled={
                      (isBnbRequest(req) && cancellingBnbBookingId === req.id) ||
                      (!isBnbRequest(req) && cancellingRequestId === req.id)
                    }
                  >
                    {isBnbRequest(req)
                      ? cancellingBnbBookingId === req.id
                        ? 'Cancelling…'
                        : 'Cancel booking'
                      : cancellingRequestId === req.id
                      ? 'Cancelling…'
                      : 'Cancel request'}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {!loading && !error && filteredRequests.length > 0 && (
        <div className="unit-requests-count">
          {filteredRequests.length} {filteredRequests.length === 1 ? 'request' : 'requests'}
          {statusFilter !== 'all' && ` (${statusFilter})`}
          {requestTypeFilter !== 'all' && ` (${requestTypeFilter.toUpperCase()})`}
        </div>
      )}

      {confirmAgreementRequest &&
        createPortal(
        <div
          className="unit-requests-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Accept agreement"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAcceptAgreementModal(e);
          }}
        >
          <div className="unit-requests-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="unit-requests-modal-header">
              <div className="unit-requests-modal-title">Accept agreement</div>
              <button type="button" className="unit-requests-modal-close" onClick={closeAcceptAgreementModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="unit-requests-modal-body">
              <div className="unit-requests-modal-subtitle">
                Please confirm you have read and understood the tenancy agreement for{' '}
                <strong>{confirmAgreementRequest.propertyName || 'this property'}</strong>.
              </div>
              <label className="unit-requests-modal-check">
                <input
                  type="checkbox"
                  checked={confirmAgreementChecked}
                  onChange={(e) => setConfirmAgreementChecked(e.target.checked)}
                />
                <span>I have read the agreement and I accept it.</span>
              </label>
              <div className="unit-requests-modal-hint">Tip: use “View agreement” to open it in a new tab.</div>
            </div>
            <div className="unit-requests-modal-actions">
              <button type="button" className="unit-requests-modal-btn unit-requests-modal-btn--secondary" onClick={closeAcceptAgreementModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unit-requests-modal-btn unit-requests-modal-btn--primary"
                onClick={confirmAcceptAgreement}
                disabled={!confirmAgreementChecked || agreeingRequestId === confirmAgreementRequest.id}
              >
                {agreeingRequestId === confirmAgreementRequest.id ? 'Accepting…' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
        , document.body)}

      {confirmCancelRequest &&
        createPortal(
        <div
          className="unit-requests-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Cancel unit request"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCancelModal(e);
          }}
        >
          <div className="unit-requests-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="unit-requests-modal-header">
              <div className="unit-requests-modal-title">Cancel request</div>
              <button type="button" className="unit-requests-modal-close" onClick={closeCancelModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="unit-requests-modal-body">
              <div className="unit-requests-modal-subtitle">
                Are you sure you want to cancel your request for{' '}
                <strong>{confirmCancelRequest.propertyName || 'this property'}</strong> —{' '}
                <strong>{confirmCancelRequest.unitName || confirmCancelRequest.unitNumber || 'Unit'}</strong>?
              </div>
              <div className="unit-requests-modal-hint">
                You can only cancel before it’s accepted by the agent/landlord.
              </div>
            </div>
            <div className="unit-requests-modal-actions">
              <button type="button" className="unit-requests-modal-btn unit-requests-modal-btn--secondary" onClick={closeCancelModal}>
                Keep request
              </button>
              <button
                type="button"
                className="unit-requests-modal-btn unit-requests-modal-btn--danger"
                onClick={confirmCancel}
                disabled={cancellingRequestId === confirmCancelRequest.id}
              >
                {cancellingRequestId === confirmCancelRequest.id ? 'Cancelling…' : 'Cancel request'}
              </button>
            </div>
          </div>
        </div>
        , document.body)}

      {confirmCancelBnbBooking &&
        createPortal(
        <div
          className="unit-requests-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Cancel BnB booking"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCancelBnbModal(e);
          }}
        >
          <div className="unit-requests-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="unit-requests-modal-header">
              <div className="unit-requests-modal-title">Cancel booking</div>
              <button type="button" className="unit-requests-modal-close" onClick={closeCancelBnbModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="unit-requests-modal-body">
              <div className="unit-requests-modal-subtitle">
                Cancel your stay at{' '}
                <strong>{confirmCancelBnbBooking.propertyName || 'this property'}</strong> —{' '}
                <strong>{confirmCancelBnbBooking.unitName || confirmCancelBnbBooking.unitNumber || 'Unit'}</strong>
                {' '}(check-in {formatDate(confirmCancelBnbBooking.checkInDate)})?
              </div>
              <label className="unit-requests-modal-reason-label" htmlFor="bnb-cancel-reason">
                Reason
              </label>
              <textarea
                id="bnb-cancel-reason"
                className="unit-requests-modal-textarea"
                rows={3}
                value={bnbCancelReason}
                onChange={(e) => setBnbCancelReason(e.target.value)}
                placeholder="e.g. Plans changed"
                disabled={cancellingBnbBookingId === confirmCancelBnbBooking.id}
              />
              <div className="unit-requests-modal-hint">This reason may be shared with the host.</div>
            </div>
            <div className="unit-requests-modal-actions">
              <button type="button" className="unit-requests-modal-btn unit-requests-modal-btn--secondary" onClick={closeCancelBnbModal}>
                Keep booking
              </button>
              <button
                type="button"
                className="unit-requests-modal-btn unit-requests-modal-btn--danger"
                onClick={confirmCancelBnb}
                disabled={
                  !String(bnbCancelReason || '').trim() ||
                  cancellingBnbBookingId === confirmCancelBnbBooking.id
                }
              >
                {cancellingBnbBookingId === confirmCancelBnbBooking.id ? 'Cancelling…' : 'Cancel booking'}
              </button>
            </div>
          </div>
        </div>
        , document.body)}

      {paymentDetailsModalRequest &&
        createPortal(
        <div
          className="unit-requests-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Payment details"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePaymentDetailsModal(e);
          }}
        >
          <div className="unit-requests-modal unit-requests-modal--wide" onMouseDown={(e) => e.stopPropagation()}>
            <div className="unit-requests-modal-header">
              <div className="unit-requests-modal-title">Payment details</div>
              <button type="button" className="unit-requests-modal-close" onClick={closePaymentDetailsModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="unit-requests-modal-body">
              <div className="unit-requests-modal-subtitle">
                Payment instructions for <strong>{paymentDetailsModalRequest.propertyName || 'this property'}</strong> —{' '}
                <strong>{paymentDetailsModalRequest.unitName || paymentDetailsModalRequest.unitNumber || 'Unit'}</strong>.
              </div>
              {loadingPaymentDetailsById?.[paymentDetailsModalRequest.id] && (
                <p>Loading payment details…</p>
              )}
              {!loadingPaymentDetailsById?.[paymentDetailsModalRequest.id] && (
                <div className="unit-request-payment-box">
                  <div className="unit-request-payment-row">
                    <span className="unit-request-payment-label">Method</span>
                    <span className="unit-request-payment-value">{paymentDetailsById[paymentDetailsModalRequest.id]?.method || '—'}</span>
                  </div>
                  <div className="unit-request-payment-row">
                    <span className="unit-request-payment-label">Till number</span>
                    <span className="unit-request-payment-value">{paymentDetailsById[paymentDetailsModalRequest.id]?.tillNumber || '—'}</span>
                  </div>
                  <div className="unit-request-payment-row">
                    <span className="unit-request-payment-label">Account name</span>
                    <span className="unit-request-payment-value">{paymentDetailsById[paymentDetailsModalRequest.id]?.accountName || '—'}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="unit-requests-modal-actions">
              <button type="button" className="unit-requests-modal-btn unit-requests-modal-btn--secondary" onClick={closePaymentDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
        , document.body)}

      {evidenceModalRequest &&
        createPortal(
        <div
          className="unit-requests-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Submit payment evidence"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEvidenceModal(e);
          }}
        >
          <div className={`unit-requests-modal ${!isBnbRequest(evidenceModalRequest) ? 'unit-requests-modal--wide' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
            <div className="unit-requests-modal-header">
              <div className="unit-requests-modal-title">Submit payment evidence</div>
              <button type="button" className="unit-requests-modal-close" onClick={closeEvidenceModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="unit-requests-modal-body unit-requests-modal-body--scroll">
              <div className="unit-requests-modal-subtitle">
                Upload proof for <strong>{evidenceModalRequest.propertyName || 'this property'}</strong> —{' '}
                <strong>{evidenceModalRequest.unitName || evidenceModalRequest.unitNumber || 'Unit'}</strong>.
              </div>

              {evidenceModalMessage && (
                <div className="unit-requests-modal-toast unit-requests-modal-toast--success" role="status">
                  {evidenceModalMessage}
                </div>
              )}

              <div className="unit-request-evidence">
                <div className={`unit-request-evidence-grid ${isBnbRequest(evidenceModalRequest) ? 'unit-request-evidence-grid--single' : ''}`}>
                  {/* --- Booking Payment Section (BnB) or Deposit Section (Rental) --- */}
                  <div className={`unit-request-evidence-card ${isBnbRequest(evidenceModalRequest) ? 'unit-request-evidence-card--compact' : ''}`}>
                    <div className="unit-request-evidence-label">
                      {isBnbRequest(evidenceModalRequest) ? 'Booking Total' : 'Deposit'}{' '}
                      {formatMoneyKES(isBnbRequest(evidenceModalRequest) ? evidenceModalRequest.totalAmount : evidenceModalRequest.depositAmount) 
                        ? `(${formatMoneyKES(isBnbRequest(evidenceModalRequest) ? evidenceModalRequest.totalAmount : evidenceModalRequest.depositAmount)})` 
                        : ''}
                    </div>
                    <div className="unit-request-evidence-row">
                      <label>
                        Amount paid
                        <input
                          type="number"
                          inputMode="numeric"
                          className="unit-request-evidence-input"
                          value={paymentsDraftById?.[evidenceModalRequest.id]?.deposit?.amountPaid ?? ''}
                          onChange={(e) => onChangeDraftField(evidenceModalRequest.id, 'deposit', 'amountPaid', e.target.value)}
                          placeholder={isBnbRequest(evidenceModalRequest) ? "Total amount" : "15000"}
                        />
                      </label>
                    </div>
                    <div className="unit-request-evidence-row">
                      <label>
                        Mpesa message (required if no photo)
                        <textarea
                          className="unit-request-evidence-textarea"
                          value={paymentsDraftById?.[evidenceModalRequest.id]?.deposit?.mpesaMessage ?? ''}
                          onChange={(e) => onChangeDraftField(evidenceModalRequest.id, 'deposit', 'mpesaMessage', e.target.value)}
                          rows={3}
                          placeholder="Paste the Mpesa confirmation message"
                        />
                      </label>
                    </div>
                    <div className="unit-request-evidence-row">
                      <label className="unit-request-evidence-file">
                        Photo (optional)
                        <input type="file" accept="image/*" onChange={(e) => uploadEvidence(e, evidenceModalRequest, 'deposit')} />
                      </label>
                      {uploadingEvidenceById?.[`${evidenceModalRequest.id}:deposit`] ? (
                        <div className="unit-request-evidence-uploading">Uploading…</div>
                      ) : paymentsDraftById?.[evidenceModalRequest.id]?.deposit?.photoUrl ? (
                        <div className="unit-request-evidence-actions">
                          <a
                            href={paymentsDraftById?.[evidenceModalRequest.id]?.deposit?.photoUrl}
                            onClick={(e) =>
                              openProtectedFile(e, paymentsDraftById?.[evidenceModalRequest.id]?.deposit?.photoUrl, 'Uploaded photo')
                            }
                            className="unit-request-evidence-link"
                          >
                            View uploaded
                          </a>
                          <button
                            type="button"
                            className="unit-request-evidence-remove"
                            onClick={(e) => removeEvidencePhoto(e, evidenceModalRequest.id, 'deposit')}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* --- First Month Section (Only for Rental) --- */}
                  {!isBnbRequest(evidenceModalRequest) && (
                    <div className="unit-request-evidence-card">
                      <div className="unit-request-evidence-label">
                        First month {formatMoneyKES(evidenceModalRequest.monthlyRent) ? `(${formatMoneyKES(evidenceModalRequest.monthlyRent)})` : ''}
                      </div>
                      <div className="unit-request-evidence-row">
                        <label>
                          Amount paid
                          <input
                            type="number"
                            inputMode="numeric"
                            className="unit-request-evidence-input"
                            value={paymentsDraftById?.[evidenceModalRequest.id]?.firstMonth?.amountPaid ?? ''}
                            onChange={(e) => onChangeDraftField(evidenceModalRequest.id, 'firstMonth', 'amountPaid', e.target.value)}
                            placeholder="30000"
                          />
                        </label>
                      </div>
                      <div className="unit-request-evidence-row">
                        <label>
                          Mpesa message (required if no photo)
                          <textarea
                            className="unit-request-evidence-textarea"
                            value={paymentsDraftById?.[evidenceModalRequest.id]?.firstMonth?.mpesaMessage ?? ''}
                            onChange={(e) => onChangeDraftField(evidenceModalRequest.id, 'firstMonth', 'mpesaMessage', e.target.value)}
                            rows={3}
                            placeholder="Paste the Mpesa confirmation message"
                          />
                        </label>
                      </div>
                      <div className="unit-request-evidence-row">
                        <label className="unit-request-evidence-file">
                          Photo (optional)
                          <input type="file" accept="image/*" onChange={(e) => uploadEvidence(e, evidenceModalRequest, 'firstMonth')} />
                        </label>
                        {uploadingEvidenceById?.[`${evidenceModalRequest.id}:firstMonth`] ? (
                          <div className="unit-request-evidence-uploading">Uploading…</div>
                        ) : paymentsDraftById?.[evidenceModalRequest.id]?.firstMonth?.photoUrl ? (
                          <div className="unit-request-evidence-actions">
                            <a
                              href={paymentsDraftById?.[evidenceModalRequest.id]?.firstMonth?.photoUrl}
                              onClick={(e) =>
                                openProtectedFile(e, paymentsDraftById?.[evidenceModalRequest.id]?.firstMonth?.photoUrl, 'Uploaded photo')
                              }
                              className="unit-request-evidence-link"
                            >
                              View uploaded
                            </a>
                            <button
                              type="button"
                              className="unit-request-evidence-remove"
                              onClick={(e) => removeEvidencePhoto(e, evidenceModalRequest.id, 'firstMonth')}
                            >
                              Remove
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                <div className="unit-request-evidence-hint">
                  {isBnbRequest(evidenceModalRequest) 
                    ? "Provide an Mpesa message or photo for your booking payment."
                    : "Provide an Mpesa message or photo for each payment. (At least one is required per section.)"}
                </div>
              </div>
            </div>

            <div className="unit-requests-modal-actions">
              <button type="button" className="unit-requests-modal-btn unit-requests-modal-btn--secondary" onClick={closeEvidenceModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unit-requests-modal-btn unit-requests-modal-btn--primary"
                onClick={(e) => submitEvidence(e, evidenceModalRequest)}
                disabled={submittingEvidenceRequestId === evidenceModalRequest.id}
              >
                {submittingEvidenceRequestId === evidenceModalRequest.id ? 'Submitting…' : 'Submit evidence'}
              </button>
            </div>
          </div>
        </div>
        , document.body)}
    </div>
  );
};

export default UnitRequests;
